import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { MapPin, Plus, Trash2, Pencil, Save, X, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { label: '', name: '', address: '', postal_code: '', region: '', phone: '', is_default: false };

export const SavedAddressesSection = () => {
  const { language } = useLanguage();
  const tr = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? ru : en);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/profile/addresses`);
      setAddresses(data.addresses || []);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(EMPTY); setEditingId(null); setError(''); setShowForm(true); };
  const openEdit = (a) => {
    setForm({ label: a.label || '', name: a.name || '', address: a.address || '',
      postal_code: a.postal_code || '', region: a.region || '', phone: a.phone || '', is_default: !!a.is_default });
    setEditingId(a.id); setError(''); setShowForm(true);
  };
  const cancel = () => { setShowForm(false); setEditingId(null); setForm(EMPTY); setError(''); };

  const save = async () => {
    if (!form.address.trim()) {
      setError(tr('Adrese ir obligāta', 'Address is required', 'Адрес обязателен'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { data } = editingId
        ? await axios.put(`${API}/profile/addresses/${editingId}`, form)
        : await axios.post(`${API}/profile/addresses`, form);
      setAddresses(data.addresses || []);
      cancel();
    } catch (e) {
      setError(tr('Neizdevās saglabāt', 'Failed to save', 'Не удалось сохранить'));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      const { data } = await axios.delete(`${API}/profile/addresses/${id}`);
      setAddresses(data.addresses || []);
    } catch (e) { /* ignore */ }
  };

  const makeDefault = async (id) => {
    try {
      const { data } = await axios.post(`${API}/profile/addresses/${id}/default`);
      setAddresses(data.addresses || []);
    } catch (e) { /* ignore */ }
  };

  const field = (key, placeholder, testid, extra = {}) => (
    <input
      value={form[key]}
      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      placeholder={placeholder}
      className="w-full h-11 px-3 rounded-xl border border-[#E5E4E0] bg-[#FAFAFA] text-sm focus:border-[#D97736] focus:outline-none"
      data-testid={testid}
      {...extra}
    />
  );

  return (
    <div className="bg-white border border-[#E5E4E0] rounded-2xl p-6 sm:p-8" data-testid="saved-addresses-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-outfit text-xl font-semibold text-[#121212] flex items-center gap-2">
          <MapPin className="w-5 h-5 text-[#D97736]" />
          {tr('Piegādes adreses', 'Delivery addresses', 'Адреса доставки')}
        </h2>
        {!showForm && (
          <Button onClick={openAdd} className="bg-[#D97736] hover:bg-[#C06528] text-white h-9" data-testid="add-address-btn">
            <Plus className="w-4 h-4 mr-1" />
            {tr('Pievienot', 'Add', 'Добавить')}
          </Button>
        )}
      </div>

      <p className="text-sm text-[#5C5C5C] mb-5">
        {tr('Šeit saglabātās adreses būs pieejamas izvēlei apmaksas laikā.',
          'Addresses saved here will be selectable at checkout.',
          'Сохранённые адреса будут доступны при оформлении заказа.')}
      </p>

      {loading ? (
        <div className="py-8 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D97736]" />
        </div>
      ) : (
        <div className="space-y-3">
          {addresses.length === 0 && !showForm && (
            <div className="text-center py-8 text-[#5C5C5C] text-sm border border-dashed border-[#E5E4E0] rounded-xl" data-testid="no-addresses">
              {tr('Vēl nav saglabātu adrešu.', 'No saved addresses yet.', 'Пока нет сохранённых адресов.')}
            </div>
          )}

          {addresses.map((a) => (
            <div key={a.id} className="flex items-start justify-between gap-3 p-4 rounded-xl border border-[#E5E4E0]" data-testid={`address-card-${a.id}`}>
              <div className="min-w-0">
                <div className="font-medium text-[#121212] flex items-center gap-2">
                  {a.label || a.name || tr('Adrese', 'Address', 'Адрес')}
                  {a.is_default && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#4A6B53]/10 text-[#4A6B53] text-xs font-medium" data-testid={`default-badge-${a.id}`}>
                      <Star className="w-3 h-3 fill-[#4A6B53]" />
                      {tr('Noklusējuma', 'Default', 'По умолчанию')}
                    </span>
                  )}
                </div>
                <div className="text-sm text-[#5C5C5C]">
                  {a.name && <span>{a.name} · </span>}
                  {a.address}{a.postal_code ? `, ${a.postal_code}` : ''}
                  {a.phone ? ` · ${a.phone}` : ''}
                </div>
                {!a.is_default && (
                  <button onClick={() => makeDefault(a.id)} className="mt-2 text-xs text-[#D97736] hover:underline flex items-center gap-1" data-testid={`set-default-${a.id}`}>
                    <Star className="w-3 h-3" />
                    {tr('Iestatīt kā noklusējumu', 'Set as default', 'Сделать по умолчанию')}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => openEdit(a)} className="p-2 text-[#5C5C5C] hover:text-[#D97736]" data-testid={`edit-address-${a.id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button className="p-2 text-[#5C5C5C] hover:text-red-500" data-testid={`delete-address-${a.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{tr('Dzēst adresi?', 'Delete address?', 'Удалить адрес?')}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {tr('Šī adrese tiks neatgriezeniski dzēsta.', 'This address will be permanently removed.', 'Адрес будет удалён навсегда.')}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{tr('Atcelt', 'Cancel', 'Отмена')}</AlertDialogCancel>
                      <AlertDialogAction className="bg-red-500 hover:bg-red-600" onClick={() => remove(a.id)} data-testid={`confirm-delete-address-${a.id}`}>
                        {tr('Dzēst', 'Delete', 'Удалить')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}

          {showForm && (
            <div className="p-4 rounded-xl border border-[#D97736] bg-[#FFF6F0] space-y-3" data-testid="address-form">
              <div className="font-medium text-[#121212]">
                {editingId ? tr('Labot adresi', 'Edit address', 'Редактировать адрес') : tr('Jauna adrese', 'New address', 'Новый адрес')}
              </div>
              {error && <div className="text-sm text-red-600" data-testid="address-form-error">{error}</div>}
              {field('label', tr('Nosaukums (piem. Mājas)', 'Label (e.g. Home)', 'Название (напр. Дом)'), 'addr-form-label')}
              {field('name', tr('Saņēmēja vārds', 'Recipient name', 'Имя получателя'), 'addr-form-name')}
              {field('address', tr('Adrese *', 'Address *', 'Адрес *'), 'addr-form-address')}
              <div className="flex gap-2">
                {field('postal_code', 'LV-1001', 'addr-form-postal', { className: 'w-1/2 h-11 px-3 rounded-xl border border-[#E5E4E0] bg-[#FAFAFA] text-sm focus:border-[#D97736] focus:outline-none' })}
                {field('phone', tr('Tālrunis', 'Phone', 'Телефон'), 'addr-form-phone', { className: 'w-1/2 h-11 px-3 rounded-xl border border-[#E5E4E0] bg-[#FAFAFA] text-sm focus:border-[#D97736] focus:outline-none' })}
              </div>
              <label className="flex items-center gap-2 text-sm text-[#5C5C5C] cursor-pointer">
                <input type="checkbox" checked={!!form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} className="accent-[#D97736]" data-testid="addr-form-default" />
                {tr('Iestatīt kā noklusējuma adresi', 'Set as default address', 'Сделать адресом по умолчанию')}
              </label>
              <div className="flex gap-2">
                <Button onClick={save} disabled={saving} className="bg-[#D97736] hover:bg-[#C06528] text-white h-10" data-testid="save-address-btn">
                  <Save className="w-4 h-4 mr-1" />
                  {saving ? tr('Saglabā...', 'Saving...', 'Сохранение...') : tr('Saglabāt', 'Save', 'Сохранить')}
                </Button>
                <Button onClick={cancel} variant="outline" className="h-10 border-[#E5E4E0]" data-testid="cancel-address-btn">
                  <X className="w-4 h-4 mr-1" />
                  {tr('Atcelt', 'Cancel', 'Отмена')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SavedAddressesSection;
